import React, { FC } from 'react';

import { FormattedMessage } from 'react-intl';
import { useTheme } from 'styled-components';

import { BaseStyle, BaseStyleEnums } from '@nhl/nhl-breakaway-ui';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import CommonMessages from '~/core/commonMessages';
import { FAKE_TBD_TEAM_ID } from '~/core/constants';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { Situation } from '~/core/types';
import LocalizedTeamName from '~/views/components/localizedTeamName/LocalizedTeamName';
import { StrongName } from '~/views/components/localizedTeamName/styles';
import TeamSituation, { DividerSide } from '~/views/components/teamSituation/TeamSituation';

import { GameCenterOrPlayoffSeriesTeam } from '../../types';

import {
    RecordText,
    ShotsAndSituationContainer,
    ShotsOnGoal,
    TeamContainer,
    TeamDisplayContainer,
    TeamNameContainer,
} from './styles';

type TeamProps = {
    hideRecord?: boolean;
    hideTricode?: boolean;
    isExpanded: boolean;
    isHome: boolean;
    isPreGame: boolean;
    isSeries?: boolean;
    situation?: Situation;
    team?: GameCenterOrPlayoffSeriesTeam | null;
};

const StylizedTeam: FC<TeamProps> = ({
    hideRecord = false,
    hideTricode = false,
    isHome,
    team = null,
    isPreGame,
    isSeries = false,
    situation,
}) => {
    const screenSize = useSizeWithThrottle();
    const isLarge = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.large;
    const theme = useTheme();

    const situationForSide = situation?.[isHome ? 'homeTeam' : 'awayTeam'];

    return (
        team && (
            <TeamContainer $isHome={isHome}>
                <TeamDisplayContainer $isHome={isHome}>
                    <TeamDisplay
                        logoUrlOverride={theme === BaseStyle.DarkTheme ? team.darkLogo : team.logo}
                        team={{ triCode: team.abbrev }}
                        displayName={false}
                    />
                    <TeamNameContainer $isHome={isHome}>
                        {isLarge ? (
                            <LocalizedTeamName
                                teamCommonName={team.commonName || team.name}
                                teamId={team.id}
                                teamPlaceNameWithPreposition={team.placeNameWithPreposition}
                                alignRight={isHome}
                            />
                        ) : (
                            !hideTricode && <StrongName>{team.abbrev}</StrongName>
                        )}
                        {!isPreGame && !isSeries && (
                            <ShotsAndSituationContainer>
                                {isHome && (
                                    <TeamSituation
                                        descriptions={situationForSide?.situationDescriptions}
                                        timeRemaining={situation?.timeRemaining}
                                        dividerSide={DividerSide.right}
                                        hideDanglingDivider
                                    />
                                )}
                                <ShotsOnGoal $isHidden={situationForSide?.situationDescriptions !== undefined}>
                                    <FormattedMessage
                                        {...CommonMessages.statsDescriptions.long.sogWithAmount}
                                        values={{ value: team.sog }}
                                    />
                                </ShotsOnGoal>
                                {!isHome && (
                                    <TeamSituation
                                        descriptions={situationForSide?.situationDescriptions}
                                        timeRemaining={situation?.timeRemaining}
                                        dividerSide={DividerSide.left}
                                        hideDanglingDivider
                                    />
                                )}
                            </ShotsAndSituationContainer>
                        )}
                        <RecordText $isHidden={hideRecord || team.id === FAKE_TBD_TEAM_ID}>{team.record}</RecordText>
                    </TeamNameContainer>
                </TeamDisplayContainer>
            </TeamContainer>
        )
    );
};

export default StylizedTeam;
